import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const CANONICAL_BASE = "https://ltvproduction.com";

// Map alternate routes to their canonical path
const CANONICAL_MAP = {
  // All privacy variants → /privacy
  "/privacy-policy": "/privacy",
  "/vod/privacy": "/privacy",
  "/vod/privacy-policy": "/privacy",
  // All terms variants → /terms
  "/terms-of-service": "/terms",
  "/vod/terms": "/terms",
  "/vod/terms-of-service": "/terms",
  // Other duplicates
  "/contact-us": "/contact",
  "/copyright": "/dmca",
  "/community-guidelines": "/content-policy",
  "/cookies": "/cookie-policy",
  "/vod/cookie-policy": "/cookie-policy",
  "/vod/about": "/about",
  "/vod/help": "/vod/faq",
};

// Pages that should NOT be indexed (user-specific, empty, or dynamic)
const NOINDEX_PATHS = [
  "/vod/history",
  "/vod/profile",
  "/vod/search",
  "/vod/signup",
  "/vod/login",
  "/vod/account",
  "/vod/checkout",
  "/vod/watch/",
  "/vod/admin",
];

function updateMetaTag(property, content) {
  if (!content) return;
  let meta = document.querySelector(`meta[property="${property}"]`) ||
             document.querySelector(`meta[name="${property}"]`);
  if (meta) {
    meta.setAttribute("content", content);
  }
}

const DynamicCanonical = ({ ogImage, ogTitle, ogDescription }) => {
  const { pathname } = useLocation();

  useEffect(() => {
    // For ltvvod.com domain, canonical to ltvproduction.com/vod
    const isLtvvod = window.location.hostname.includes("ltvvod.com");
    let canonicalPath = pathname;

    if (isLtvvod && canonicalPath === "/") {
      canonicalPath = "/vod";
    } else if (isLtvvod && !canonicalPath.startsWith("/vod")) {
      canonicalPath = "/vod" + canonicalPath;
    }

    // Resolve duplicate routes to canonical path
    canonicalPath = CANONICAL_MAP[canonicalPath] || canonicalPath;

    // Remove trailing slash (except for root)
    if (canonicalPath.length > 1 && canonicalPath.endsWith("/")) {
      canonicalPath = canonicalPath.slice(0, -1);
    }

    const canonicalUrl = CANONICAL_BASE + canonicalPath;

    // Find or create the canonical link element
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonicalUrl);

    // Update OG URL
    updateMetaTag("og:url", canonicalUrl);

    // Add/remove noindex for user-specific pages
    let robotsMeta = document.querySelector('meta[name="robots"]');
    const shouldNoindex = NOINDEX_PATHS.some(p => pathname === p || pathname.startsWith(p));
    if (shouldNoindex) {
      if (!robotsMeta) {
        robotsMeta = document.createElement("meta");
        robotsMeta.setAttribute("name", "robots");
        document.head.appendChild(robotsMeta);
      }
      robotsMeta.setAttribute("content", "noindex, nofollow");
    } else if (robotsMeta) {
      robotsMeta.remove();
    }
  }, [pathname]);

  // Update OG image when prop changes (e.g., video thumbnail on video pages)
  useEffect(() => {
    if (ogImage) {
      updateMetaTag("og:image", ogImage);
      updateMetaTag("twitter:image", ogImage);
    }
  }, [ogImage]);

  useEffect(() => {
    if (ogTitle) {
      updateMetaTag("og:title", ogTitle);
      updateMetaTag("twitter:title", ogTitle);
    }
  }, [ogTitle]);

  useEffect(() => {
    if (ogDescription) {
      updateMetaTag("og:description", ogDescription);
      updateMetaTag("twitter:description", ogDescription);
    }
  }, [ogDescription]);

  return null;
};

export default DynamicCanonical;
